import React from 'react';
import { Bot } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AITypingIndicatorProps {
  mode: 'auto' | 'suggestions';
  className?: string;
  isOutgoing?: boolean; // Controls right vs left alignment
}

export function AITypingIndicator({ mode, className, isOutgoing = true }: AITypingIndicatorProps) {
  const modeLabels = {
    auto: 'AI Agent',
    suggestions: 'AI Assistant'
  };

  if (isOutgoing) {
    // Right-side layout (agent/platform side)
    return (
      <div className={cn(
        "flex items-start gap-3 py-3 px-4 animate-fade-in justify-end",
        className
      )}>
        {/* Typing indicator content */}
        <div className="flex-1 space-y-1 flex flex-col items-end">
          {/* AI name and mode */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {mode === 'auto' ? 'is typing...' : 'is thinking...'}
            </span>
            <span className="text-sm font-medium text-foreground">
              {modeLabels[mode]}
            </span>
          </div>
          
          {/* Animated dots */}
          <div className="flex items-center gap-1 py-2 px-3 bg-primary/10 rounded-2xl rounded-tr-sm max-w-fit">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce"></div>
            </div>
          </div>
        </div>
        
        {/* AI Avatar */}
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground">
          <Bot className="w-4 h-4" />
        </div>
      </div>
    );
  }

  // Left-side layout (customer side) - fallback
  return (
    <div className={cn(
      "flex items-start gap-3 py-3 px-4 animate-fade-in",
      className
    )}>
      {/* AI Avatar */}
      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground">
        <Bot className="w-4 h-4" />
      </div>
      
      {/* Typing indicator content */}
      <div className="flex-1 space-y-1">
        {/* AI name and mode */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {modeLabels[mode]}
          </span>
          <span className="text-xs text-muted-foreground">
            {mode === 'auto' ? 'is typing...' : 'is thinking...'}
          </span>
        </div>
        
        {/* Animated dots */}
        <div className="flex items-center gap-1 py-2 px-3 bg-muted rounded-2xl rounded-tl-sm max-w-fit">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>
    </div>
  );
}